/**
 * A LEAD'S LENS AGAINST THE PAGE'S. Following a lead, the tables always look
 * through it: its tags are held in the lens, and anything you add narrows
 * further. Pure, so jest can pin it.
 */
import type { BoardroomLead, BoardroomLeadLens } from '../../../types';
import type { View } from '../filters';

/** The lens as a lead stores it, from the page's view. */
export function lensOfView(view: View): BoardroomLeadLens {
  return {
    tags: [...view.tags],
    verified: view.verified ?? null,
    by: view.by ?? null,
    sourced: view.sourced ?? null,
  };
}

/** Whether the view looks through everything the lead narrows by. */
export function insideLead(view: View, lead: BoardroomLead | null | undefined): boolean {
  if (!lead) return true;
  const l = lead.lens;
  if (!l.tags.every(t => view.tags.includes(t))) return false;
  if (l.verified && view.verified !== l.verified) return false;
  if (l.by && view.by !== l.by) return false;
  if (l.sourced && view.sourced !== l.sourced) return false;
  return true;
}

/** The view with the lead held in: the lead's terms, then the view's own.
 *  Terms all hold, so adding them can only narrow. */
export function withLead(view: View, lead: BoardroomLead | null | undefined): View {
  if (!lead) return view;
  const l = lead.lens;
  return {
    ...view,
    tags: [...l.tags, ...view.tags.filter(t => !l.tags.includes(t))],
    verified: (l.verified as View['verified']) ?? view.verified,
    by: l.by ?? view.by,
    sourced: (l.sourced as View['sourced']) ?? view.sourced,
  };
}

/** The view as the lead leaves it: exactly its lens, or nothing for the whole
 *  room. Where going to a lead (or back) puts the tables. */
export function viewForLead(view: View, lead: BoardroomLead | null | undefined): View {
  const bare: View = { ...view, tags: [], verified: null, by: null, sourced: null };
  return withLead(bare, lead);
}

/** Whether the view narrows past the lead - so there is a lead inside it to
 *  follow. With no lead, any lens at all. */
export function narrowsPast(view: View, lead: BoardroomLead | null | undefined): boolean {
  const l = lead?.lens;
  const extraTags = view.tags.filter(t => !(l?.tags ?? []).includes(t)).length > 0;
  return (
    extraTags ||
    (Boolean(view.verified) && !l?.verified) ||
    (Boolean(view.by) && !l?.by) ||
    (Boolean(view.sourced) && !l?.sourced)
  );
}
